/* ============================================================================
   Eddia — Interactions layer (controller)
   ----------------------------------------------------------------------------
   Lightweight, dependency-free motion controller. Pairs with
   assets/eddia-interactions.css. Only runs when html.eddia-motion is present
   (set by the inline enabler in theme.liquid when the merchant setting is on and
   the visitor has no reduced-motion preference).

   Design rules:
   • IntersectionObserver only — no scroll handlers for reveals.
   • One shared rAF-throttled scroll callback for the (optional) progress bar and
     parallax; both are transform-only.
   • Fully guarded: any error tears the whole layer down and reveals all content,
     so the page can never be left with hidden elements.
   ========================================================================== */
(function () {
  "use strict";

  var root = document.documentElement;
  if (!root.classList.contains("eddia-motion")) return; // reduced motion / disabled

  var cfg = window.eddiaMotionConfig || {};
  var CFG = {
    reveal:   cfg.reveal   !== false,
    parallax: cfg.parallax !== false,
    progress: cfg.progress !== false
  };
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* --- failsafe: on any error, reveal everything and stop -------------------- */
  function revealAll() {
    try {
      document.querySelectorAll("[data-eddia-reveal]").forEach(function (el) {
        el.classList.add("is-in");
      });
      document.querySelectorAll(".eddia-steps__reveal").forEach(function (el) {
        el.classList.add("is-active");
      });
    } catch (e) {}
  }
  function panic(err) {
    if (window.console && console.warn) console.warn("[eddia-motion] disabled:", err);
    root.classList.remove("eddia-motion", "eddia-reveal-on", "eddia-hero-intro");
    revealAll();
  }

  var prefersReduced = false;
  try {
    prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {}
  if (prefersReduced) { panic("reduced-motion"); return; }

  var supportsIO = "IntersectionObserver" in window;

  /* ========================================================================
     1) Reveal on scroll (utilities + staggered groups)
     ====================================================================== */
  var revealObserver = null;

  function markIn(el) {
    el.classList.add("is-in");
  }

  function setupReveals(scope) {
    if (!CFG.reveal) return;
    scope = scope || document;

    // stagger groups: children cascade together via --eddia-i
    scope.querySelectorAll("[data-eddia-stagger]").forEach(function (group) {
      if (group.__eddiaInit) return;
      group.__eddiaInit = true;
      var kids = group.querySelectorAll("[data-eddia-reveal]");
      kids.forEach(function (k, i) { k.style.setProperty("--eddia-i", i); });
      if (designMode || !supportsIO) { kids.forEach(markIn); return; }
      revealObserver.observe(group);
    });

    // solo reveals (not inside a stagger group)
    scope.querySelectorAll("[data-eddia-reveal]").forEach(function (el) {
      if (el.__eddiaInit) return;
      if (el.closest("[data-eddia-stagger]")) return;
      el.__eddiaInit = true;
      if (designMode || !supportsIO) { markIn(el); return; }
      revealObserver.observe(el);
    });
  }

  /* ========================================================================
     2) How It Works — step activation + connector fill
     ====================================================================== */
  var stepObserver = null;

  function setupSteps(scope) {
    if (!CFG.reveal) return;
    scope = scope || document;
    scope.querySelectorAll(".eddia-steps").forEach(function (list) {
      if (list.__eddiaInit) return;
      list.__eddiaInit = true;
      list.classList.add("eddia-steps__reveal");
      var steps = list.querySelectorAll(".eddia-step");
      steps.forEach(function (s, i) { s.style.setProperty("--eddia-i", i); });

      if (designMode || !supportsIO) { list.classList.add("is-active"); return; }
      stepObserver.observe(list);
    });
  }

  /* ========================================================================
     2a) Retail & Wholesale — odometer quantities. Rebuilds "1–99" / "100+" as
     mechanical counter reels: each digit becomes a masked strip of 0-9 twice
     over, so CSS can spin it a full cycle and land it on the real digit.
     The visible reels are aria-hidden and the original text is kept in an
     .sr-only span, so screen readers still hear "100+" and not the strip.
     Deliberately conservative — anything that isn't a bare quantity (rich
     text, letters, long strings) is left exactly as the merchant wrote it,
     and if this never runs the quantity is simply plain text.
     ====================================================================== */
  var QTY_SAFE = /^[0-9\s+\-–—.,/]+$/;

  function setupOdometer(scope) {
    if (!CFG.reveal) return;
    (scope || document).querySelectorAll(".eddia-volume-tiers__qty").forEach(function (el) {
      if (el.__eddiaOdo) return;
      el.__eddiaOdo = true;
      if (el.children.length) return; // merchant used inline formatting: leave it
      var txt = (el.textContent || "").trim();
      if (!txt || txt.length > 8) return;
      if (!/[0-9]/.test(txt) || !QTY_SAFE.test(txt)) return;

      var reels = document.createElement("span");
      reels.className = "eddia-odo-row";
      reels.setAttribute("aria-hidden", "true");

      for (var i = 0; i < txt.length; i++) {
        var ch = txt.charAt(i);
        var mask = document.createElement("span");
        mask.className = "eddia-odo";
        mask.style.setProperty("--eddia-c", i);
        var reel = document.createElement("span");
        reel.className = "eddia-odo__reel";

        if (ch >= "0" && ch <= "9") {
          for (var n = 0; n < 20; n++) {
            var cell = document.createElement("span");
            cell.className = "eddia-odo__d";
            cell.textContent = String(n % 10);
            reel.appendChild(cell);
          }
          // land on the digit in the SECOND cycle, so every numeral spins a
          // full revolution first — even a 0 or a 1
          mask.style.setProperty("--eddia-d", 10 + Number(ch));
        } else {
          mask.classList.add("eddia-odo--char");
          var only = document.createElement("span");
          only.className = "eddia-odo__d";
          only.textContent = ch === " " ? " " : ch;
          reel.appendChild(only);
        }

        mask.appendChild(reel);
        reels.appendChild(mask);
      }

      var sr = document.createElement("span");
      sr.className = "sr-only";
      sr.textContent = txt;

      el.textContent = "";
      el.appendChild(sr);
      el.appendChild(reels);
    });
  }

  /* ========================================================================
     2i) Footer link roll — gives each footer link a second, identical copy of
     its own word parked directly beneath it, so CSS can lift the pair and the
     link arrives at full strength instead of merely brightening.

     The copy is aria-hidden and the original text node is what stays first in
     the DOM, so nothing is announced twice and the link's accessible name is
     unchanged. Anything that already contains markup (an icon, a badge) is
     left alone, and if this never runs the links are plain text that still
     brighten on hover exactly as the theme intends.
     ====================================================================== */
  function setupRoll(scope) {
    (scope || document).querySelectorAll(".footer__block--links .link-faded").forEach(function (a) {
      if (a.__eddiaRoll) return;
      a.__eddiaRoll = true;
      if (a.children.length) return;
      var txt = (a.textContent || "").trim();
      if (!txt || txt.length > 42) return;

      var wrap = document.createElement("span");
      wrap.className = "eddia-roll";
      var one = document.createElement("span");
      one.className = "eddia-roll__t";
      one.textContent = txt;
      var two = document.createElement("span");
      two.className = "eddia-roll__t eddia-roll__t--dup";
      two.setAttribute("aria-hidden", "true");
      two.textContent = txt;
      wrap.appendChild(one);
      wrap.appendChild(two);

      a.textContent = "";
      a.appendChild(wrap);
    });
  }

  /* ========================================================================
     2h) Proximity ("what you are looking at") — the touch stand-in for hover.
     A finger has no cursor to hover with, but it has scroll: whatever sits in
     the middle band of the screen IS what the visitor is looking at. This
     observer marks that element with .eddia-near, and the touch block in
     eddia-interactions.css re-expresses every hover effect against it, so a
     phone gets the same page rather than a static one.

     Only runs on hover-less devices — a mouse already has the real thing.
     ====================================================================== */
  var NEAR_SELECTOR = ".media-grid__item, .eddia-volume-tiers__tier, .eddia-category__link, .blog-post-card, [class*='ai-luxury-features__column-']";
  var nearObserver = null;

  function setupNear(scope) {
    if (!supportsIO || designMode) return;
    var touch = false;
    try { touch = window.matchMedia("(hover: none)").matches; } catch (e) {}
    if (!touch) return;
    if (!nearObserver) {
      nearObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          // stays marked while it is crossing the middle of the screen, and
          // lets go on the way out — so the page reads as a moving spotlight
          entry.target.classList.toggle("eddia-near", entry.isIntersecting);
        });
      }, { rootMargin: "-42% 0px -42% 0px", threshold: 0 });
    }
    (scope || document).querySelectorAll(NEAR_SELECTOR).forEach(function (el) {
      if (el.__eddiaNear) return;
      el.__eddiaNear = true;
      nearObserver.observe(el);
    });
  }

  /* ========================================================================
     2b) Word-mask title reveal (Personalization section) — wraps each word of
     the plain-text display title in a masked span; CSS raises them in
     sequence when the title's own reveal lands (.is-in). Plain text only —
     rich-text titles are left untouched. Without JS the title simply uses the
     block reveal, so nothing can be left hidden.
     ====================================================================== */
  function setupWordReveal(scope) {
    if (!CFG.reveal) return;
    (scope || document).querySelectorAll(".multiple-media-with-text .prose .h3[data-eddia-reveal]").forEach(function (h) {
      if (h.__eddiaWords) return;
      h.__eddiaWords = true;
      if (h.children.length) return; // inline formatting present: skip
      var words = (h.textContent || "").trim().split(/\s+/);
      if (words.length < 2 || words.length > 8) return;
      // Masks use nowrap, so a word that could never fit a narrow column must
      // fall back to the plain block reveal instead of overflowing.
      var longest = 0;
      words.forEach(function (w) { longest = Math.max(longest, w.length); });
      if (longest > 16) return;
      h.textContent = "";
      words.forEach(function (w, i) {
        var mask = document.createElement("span");
        mask.className = "eddia-w";
        var inner = document.createElement("span");
        inner.className = "eddia-w__i";
        inner.textContent = w;
        inner.style.setProperty("--eddia-wi", i);
        mask.appendChild(inner);
        h.appendChild(mask);
        if (i < words.length - 1) h.appendChild(document.createTextNode(" "));
      });
    });
  }

  /* ========================================================================
     2c) Pointer depth (desktop, mouse only) — the media group gently rotates
     toward the cursor; CSS consumes --eddia-tx/--eddia-ty (unitless -1..1).
     One rAF-throttled listener per section; resets on leave.
     ====================================================================== */
  function setupTilt(scope) {
    if (!CFG.parallax || designMode) return;
    (scope || document).querySelectorAll(".multiple-media-with-text").forEach(function (sec) {
      if (sec.__eddiaTilt) return;
      sec.__eddiaTilt = true;
      var wrap = sec.querySelector(".multiple-media-with-text__media-wrapper");
      if (!wrap) return;
      var raf = null, tx = 0, ty = 0;
      function apply() {
        raf = null;
        wrap.style.setProperty("--eddia-tx", tx.toFixed(3));
        wrap.style.setProperty("--eddia-ty", ty.toFixed(3));
      }
      sec.addEventListener("pointermove", function (e) {
        if (!desktopMQ || !desktopMQ.matches) return;
        if (e.pointerType && e.pointerType !== "mouse") return;
        var r = sec.getBoundingClientRect();
        if (!r.width || !r.height) return;
        tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
        ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
        if (!raf) raf = window.requestAnimationFrame(apply);
      }, { passive: true });
      sec.addEventListener("pointerleave", function () {
        tx = 0; ty = 0;
        if (!raf) raf = window.requestAnimationFrame(apply);
      }, { passive: true });
    });
  }


  /* --- 12) Mega menu showroom: pointer depth on the dark stage ---------------
     Drives --eddia-mm-px / --eddia-mm-py (-1..1) on each open .eddia-mm panel;
     eddia.css turns them into a few px of photo drift against the cursor and
     a slow counter-drift of the ghost watermark. rAF-throttled, transform-only,
     desktop fine-pointer only, and gated by the merchant's parallax toggle. */
  function setupShowroom(scope) {
    if (!CFG.parallax) return;
    var fine;
    try { fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches; } catch (e) { fine = false; }
    if (!fine) return;
    (scope || document).querySelectorAll(".mega-menu.eddia-mm").forEach(function (panel) {
      if (panel.__eddiaShowroom) return;
      panel.__eddiaShowroom = true;
      var raf = 0, px = 0, py = 0;
      function apply() {
        raf = 0;
        panel.style.setProperty("--eddia-mm-px", px.toFixed(3));
        panel.style.setProperty("--eddia-mm-py", py.toFixed(3));
      }
      panel.addEventListener("pointermove", function (e) {
        var r = panel.getBoundingClientRect();
        if (!r.width || !r.height) return;
        px = ((e.clientX - r.left) / r.width) * 2 - 1;
        py = ((e.clientY - r.top) / r.height) * 2 - 1;
        if (!raf) raf = window.requestAnimationFrame(apply);
      }, { passive: true });
      panel.addEventListener("pointerleave", function () {
        px = 0; py = 0;
        if (!raf) raf = window.requestAnimationFrame(apply);
      }, { passive: true });
    });
  }


  /* --- 13) Mega menu: instant close when the cursor leaves the panel ---------
     The theme already closes a hover menu on mouseout, but only after a 250ms
     tolerance plus a 0.4s fade — leaving the open panel feels like the menu
     is ignoring the hand. This listens for a true pointerleave on the panel
     itself and closes through the component's own toggle() (so the native
     hide animation still runs). Moving from the panel back up onto the same
     menu item does NOT close it — that path stays governed by the theme's
     hover logic, which prevents an open/close flicker. Fine pointers only;
     touch never opens these panels by hover in the first place. */
  function setupMenuLeave(scope) {
    var fine;
    try { fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches; } catch (e) { fine = false; }
    if (!fine) return;
    (scope || document).querySelectorAll("mega-menu-disclosure").forEach(function (disc) {
      if (disc.__eddiaLeave) return;
      disc.__eddiaLeave = true;
      var details = disc.querySelector("details");
      var panel = disc.querySelector(".mega-menu");
      if (!details || !panel) return;
      var blocking = false;
      // While the cooldown runs, swallow mouseover in the CAPTURE phase so it
      // never reaches the theme's hover listener on the <details>. Without
      // this, the cursor crossing the menu item on its way out snaps the
      // panel straight back open — the closes-opens-closes flicker.
      function block(ev) {
        ev.stopPropagation();
      }
      panel.addEventListener("pointerleave", function (e) {
        if (!details.open) return;
        var to = e.relatedTarget;
        if (to && disc.contains(to)) return; // back towards the header item
        if (typeof disc.toggle === "function") disc.toggle(false);
        // Drop [open] NOW instead of letting the theme's 0.4s fade keep the
        // panel alive. Two reasons, both flicker sources: (1) the theme's own
        // pending 250ms mouseout timer would call close() again mid-fade and
        // RESTART the fade from full opacity — the panel visibly blinks back;
        // close() guards on the attribute, so with it gone that second call
        // is a no-op. (2) a fading panel still hit-tests, so a grazing cursor
        // could hover it and snap it back open. Instant hide is also exactly
        // the behaviour asked for: leave the panel, it is gone.
        details.removeAttribute("open");
        if (!blocking) {
          blocking = true;
          disc.addEventListener("mouseover", block, true);
          window.setTimeout(function () {
            blocking = false;
            disc.removeEventListener("mouseover", block, true);
          }, 500);
        }
      });
    });
  }


  /* ========================================================================
     2d-hero) Sewn-in headline — wraps every letter of the hero title so CSS
     can "stitch" them in one by one (blur → sharp rise). Word wrappers keep
     natural line-wrapping intact; plain-text titles only. When this runs the
     h1 gets .eddia-lh, which switches off the block-level rise — if JS never
     runs, the original block rise still plays, so nothing is ever hidden.
     ====================================================================== */
  function setupHeroLetters() {
    var h = document.querySelector(".slideshow__slide.is-selected .prose > .h1");
    if (!h || h.__eddiaLetters) return;
    h.__eddiaLetters = true;
    if (h.children.length) return; // rich text: keep block reveal
    var words = (h.textContent || "").trim().split(/\s+/);
    if (!words.length || words.length > 12) return;
    var li = 0;
    h.textContent = "";
    words.forEach(function (w, wi) {
      var word = document.createElement("span");
      word.className = "eddia-hw";
      Array.prototype.forEach.call(w, function (ch) {
        var l = document.createElement("span");
        l.className = "eddia-l";
        l.textContent = ch;
        l.style.setProperty("--eddia-li", li++);
        word.appendChild(l);
      });
      h.appendChild(word);
      if (wi < words.length - 1) h.appendChild(document.createTextNode(" "));
    });
    h.classList.add("eddia-lh");
  }

  /* ========================================================================
     2e) Viewfinder lamp (desktop, mouse only) — a soft light follows the
     cursor across the "Precision in Every Stitch" photograph. Writes px
     offsets into --eddia-lx/--eddia-ly; CSS moves the disc with `translate`
     alone, so the full-bleed layer is never repainted. One rAF-throttled
     listener per section, and the lamp only ever fades in on hover, so if
     this never runs the section is simply a framed photograph.
     ====================================================================== */
  function setupLamp(scope) {
    if (designMode) return;
    (scope || document).querySelectorAll(".eddia-viewfinder").forEach(function (vf) {
      if (vf.__eddiaLamp) return;
      vf.__eddiaLamp = true;
      var lamp = vf.querySelector(".eddia-viewfinder__lamp");
      var host = vf.parentElement;
      if (!lamp || !host) return;
      var raf = null, lx = 0, ly = 0;
      function apply() {
        raf = null;
        lamp.style.setProperty("--eddia-lx", lx.toFixed(1) + "px");
        lamp.style.setProperty("--eddia-ly", ly.toFixed(1) + "px");
      }
      host.addEventListener("pointermove", function (e) {
        if (!desktopMQ || !desktopMQ.matches) return;
        if (e.pointerType && e.pointerType !== "mouse") return;
        var r = host.getBoundingClientRect();
        if (!r.width || !r.height) return;
        lx = e.clientX - r.left;
        ly = e.clientY - r.top;
        if (!raf) raf = window.requestAnimationFrame(apply);
      }, { passive: true });
    });
  }

  /* ========================================================================
     2g) Drifting smoke ("Precision in Every Stitch"). A real particle system
     on a canvas inside the viewfinder layer: haze rises across the photograph
     on its own, and the CURSOR'S VELOCITY is injected into every particle near
     it, so moving the mouse genuinely pushes and swirls the smoke instead of
     just spawning more of it.

     On cost, the measures worth keeping are the ones that buy real frames for
     no visual price at all:
       • the loop is dead while the section is off screen (IO) — free;
       • ONE puff sprite is pre-rendered and reused, so a frame is drawImage
         calls and nothing else; no gradient is ever built at runtime;
       • the backing store is capped at 1.5x. Smoke has no hard edge anywhere
         in it, so full retina density costs ~78% more fill and returns
         nothing the eye can find;
       • the frame delta is clamped, so a backgrounded tab cannot come back and
         teleport every particle across the frame.
     What is NOT worth saving is the effect itself: narrow viewports still get
     the atmosphere, just at a smaller budget, because there is no cursor there
     to answer and no reason to make a phone render a full simulation.
     Reduced motion never gets here — the whole layer bails out before boot.
     ====================================================================== */
  var SMOKE_MAX = 220;

  // `kind` picks the grain of the stamp. Ambient haze wants a few broad, soft
  // masses — it is atmosphere seen at a distance. A plume is seen close and
  // moving, so it wants many small clumps and long thin streaks: smoke resolves
  // into threads and shreds near its source, and a handful of big soft discs
  // never reads as anything but a puff, however well it is animated.
  // `palette` is the second half of the idea. Smoke leaving a fire is LIT BY
  // IT: the first stretch carries the flame's own colour and only cools to
  // neutral ash as it climbs away. So a plume gets two stamp sets — an ember
  // set and an ash set — and the draw loop cross-fades between them on the
  // particle's remaining heat.
  var SMOKE_PALETTES = {
    soot:  { body: ["22,19,17", "58,52,47"], wisp: ["30,27,24", "72,66,60"], bodyA: 0.30, bodyR: 0.38, wispA: 0.30 },
    ash:   { body: ["38,35,32", "84,79,73"], wisp: ["52,48,44", "104,98,91"], bodyA: 0.26, bodyR: 0.32, wispA: 0.26 },
    ember: { body: ["255,150,58", "255,96,26"], wisp: ["255,190,110", "255,124,40"], bodyA: 0.22, bodyR: 0.26, wispA: 0.20 }
  };

  function makeSmokeSprites(kind, paletteName) {
    var fine = kind === "plume";
    var pal = SMOKE_PALETTES[paletteName || "soot"];
    var out = [];
    for (var v = 0; v < 3; v++) {
      var s = fine ? 128 : 160;
      var c = document.createElement("canvas");
      c.width = s;
      c.height = s;
      var g = c.getContext("2d");
      if (!g) return null;
      // pass 1 — the body: clustered blobs biased towards one side, so every
      // variant is a lopsided, billowing silhouette instead of a perfect disc.
      // Three variants + random mirroring at draw time is what kills the "same
      // stamp everywhere" tell of fake smoke. A plume uses many more, smaller
      // clumps: near its source smoke is shredded, not smooth.
      var blobs = fine ? 40 : 22;
      var bias = Math.random() * Math.PI * 2;
      for (var i = 0; i < blobs; i++) {
        var a = Math.random() * Math.PI * 2;
        var rr = Math.pow(Math.random(), 0.65) * s * 0.24 + (Math.cos(a - bias) + 1) * s * 0.035;
        var cx = s / 2 + Math.cos(a) * rr;
        var cy = s / 2 + Math.sin(a) * rr;
        var rad = s * (fine ? 0.045 + Math.random() * 0.1 : 0.1 + Math.random() * 0.16);
        var grd = g.createRadialGradient(cx, cy, 0, cx, cy, rad);
        var col = Math.random() < 0.7 ? pal.body[0] : pal.body[1];
        var body = pal.bodyA + Math.random() * pal.bodyR;
        grd.addColorStop(0, "rgba(" + col + "," + body.toFixed(2) + ")");
        grd.addColorStop(1, "rgba(" + col + ",0)");
        g.fillStyle = grd;
        g.beginPath();
        g.arc(cx, cy, rad, 0, Math.PI * 2);
        g.fill();
      }
      // pass 2 — wisps: squashed, rotated gradients read as the stretched
      // filaments real smoke pulls off its own edges. A plume gets more of
      // them and thinner, which is what turns a puff into shreds.
      var wisps = fine ? 16 : 7;
      for (var w = 0; w < wisps; w++) {
        var wa = Math.random() * Math.PI * 2;
        var wr = s * (0.12 + Math.random() * 0.22);
        g.save();
        g.translate(s / 2 + Math.cos(wa) * wr, s / 2 + Math.sin(wa) * wr);
        g.rotate(Math.random() * Math.PI);
        g.scale(1, fine ? 0.1 + Math.random() * 0.16 : 0.3 + Math.random() * 0.25);
        var wrad = s * (0.12 + Math.random() * 0.14);
        var wg = g.createRadialGradient(0, 0, 0, 0, 0, wrad);
        var wcol = Math.random() < 0.5 ? pal.wisp[0] : pal.wisp[1];
        wg.addColorStop(0, "rgba(" + wcol + "," + pal.wispA.toFixed(2) + ")");
        wg.addColorStop(1, "rgba(" + wcol + ",0)");
        g.fillStyle = wg;
        g.beginPath();
        g.arc(0, 0, wrad, 0, Math.PI * 2);
        g.fill();
        g.restore();
      }
      // feather the whole stamp so no blob or wisp edge can ever show
      g.globalCompositeOperation = "destination-in";
      var mask = g.createRadialGradient(s / 2, s / 2, s * 0.05, s / 2, s / 2, s * 0.5);
      mask.addColorStop(0, "rgba(0,0,0,1)");
      mask.addColorStop(0.6, "rgba(0,0,0,0.94)");
      mask.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = mask;
      g.fillRect(0, 0, s, s);
      out.push(c);
    }
    return out;
  }

  /* CURL NOISE. The old field was a plain sum of sines sampled at (x, y): it
     has divergence, so particles pile up wherever it happens to point inward
     and thin out where it points away — the tell that made the smoke read as
     drifting fog rather than fluid. This samples a scalar potential instead and
     takes its PERPENDICULAR gradient, which is divergence-free by construction:
     the field can rotate and fold as much as it likes but can never compress.
     Two octaves, because real smoke rolls at a large scale and frays at a small
     one at the same time. Both terms are exact derivatives of sines, so it
     costs four trig calls and no noise table. */
  function curlX(x, y, t) {
    return -0.0071 * Math.sin(0.0062 * x + t * 0.9) * Math.sin(0.0071 * y - t * 0.7)
           - 0.0073 * Math.sin(0.0195 * x - t * 2.1) * Math.sin(0.0173 * y + t * 1.7);
  }
  function curlY(x, y, t) {
    return -0.0062 * Math.cos(0.0062 * x + t * 0.9) * Math.cos(0.0071 * y - t * 0.7)
           - 0.0082 * Math.cos(0.0195 * x - t * 2.1) * Math.cos(0.0173 * y + t * 1.7);
  }

  function setupSmoke(scope) {
    if (designMode) return;
    (scope || document).querySelectorAll(".eddia-viewfinder").forEach(function (vf) {
      if (vf.__eddiaSmoke) return;
      vf.__eddiaSmoke = true;

      var canvas = document.createElement("canvas");
      canvas.className = "eddia-viewfinder__smoke";
      canvas.setAttribute("aria-hidden", "true");
      var ctx = null;
      try { ctx = canvas.getContext("2d"); } catch (e) {}
      var sprites = ctx ? makeSmokeSprites() : null;
      var spritesAsh = ctx ? makeSmokeSprites("plume", "ash") : null;
      var spritesEmber = ctx ? makeSmokeSprites("plume", "ember") : null;
      if (!ctx || !sprites || !sprites.length) return;
      vf.insertBefore(canvas, vf.firstChild);

      var host = vf.parentElement || vf;
      var parts = [];
      // Embers. A thing that smoulders throws the occasional spark, and one
      // pinpoint of live orange climbing through grey smoke does more for the
      // illusion of fire than any amount of extra haze. Kept few and short —
      // they are punctuation, not weather — and drawn additively so they read
      // as light rather than as paint.
      var embers = [];
      var W = 0, H = 0;
      var dens = Math.min(window.devicePixelRatio || 1, 1.5);
      var raf = null, last = 0, onScreen = false, spawnAcc = 0, trailAcc = 0, emberAcc = 0;
      var mx = -1, my = -1, mvx = 0, mvy = 0, hasPointer = false;
      var lastScroll = null, gustAcc = 0;
      var airX = 0, airY = 0, lastGamma = null, lastBeta = null, motionArmed = false;

      // THE PHONE ITSELF IS ALSO WIND. Tilting or moving the device slides
      // the air, and the haze streams against it. Orientation deltas (deg)
      // become an air velocity that decays each frame, so a tilt is a gust,
      // not a permanent gale. iOS demands a user-gesture permission prompt —
      // armMotion() is called from the existing touch handler for that; on
      // engines without the permission API it arms immediately below.
      function onOrient(e) {
        if (e.gamma == null || e.beta == null) return;
        if (!onScreen) { lastGamma = e.gamma; lastBeta = e.beta; return; }
        if (lastGamma !== null) {
          var dg = e.gamma - lastGamma;
          var db = e.beta - lastBeta;
          if (dg > 20) dg = 20; if (dg < -20) dg = -20;
          if (db > 20) db = 20; if (db < -20) db = -20;
          airX += dg * 30;
          airY += db * 24;
          if (airX > 900) airX = 900; if (airX < -900) airX = -900;
          if (airY > 900) airY = 900; if (airY < -900) airY = -900;
        }
        lastGamma = e.gamma;
        lastBeta = e.beta;
      }

      var orientSeen = false;

      // devicemotion fallback: some engines deliver rotationRate more readily
      // than orientation angles. Only used while orientation stays silent.
      function onMotion(e) {
        if (orientSeen || !onScreen) return;
        var rr = e.rotationRate;
        if (!rr) return;
        var gx = rr.gamma || 0;
        var gy = rr.beta || 0;
        if (gx > 200) gx = 200; if (gx < -200) gx = -200;
        if (gy > 200) gy = 200; if (gy < -200) gy = -200;
        airX += gx * 0.6;
        airY += gy * 0.5;
        if (airX > 900) airX = 900; if (airX < -900) airX = -900;
        if (airY > 900) airY = 900; if (airY < -900) airY = -900;
      }

      function attachMotion() {
        if (motionArmed) return;
        motionArmed = true;
        window.addEventListener("deviceorientation", function (e) {
          if (e.gamma != null || e.beta != null) orientSeen = true;
          onOrient(e);
        }, { passive: true });
        window.addEventListener("devicemotion", onMotion, { passive: true });
      }

      // Callable from every tap until a listener is actually attached: a
      // dismissed prompt or an early denial does not lock the feature out
      // for the rest of the visit. (iOS shows the system prompt at most
      // once per page load; later calls just resolve with the stored state.)
      // A storefront must never surprise a customer with a permission
      // dialog. Where the gyroscope is free (Android), tilt drives the air;
      // where Apple gates it behind a prompt (iOS), we simply do not ask —
      // the ambient breeze in the frame loop carries the motion feel there
      // instead, with no sensors and no questions.
      function armMotion() {
        if (motionArmed) return;
        try {
          if (typeof DeviceOrientationEvent === "undefined") { motionArmed = true; return; }
          if (typeof DeviceOrientationEvent.requestPermission === "function") {
            motionArmed = true;
            return;
          }
          attachMotion();
        } catch (err) {}
      }

      // No permission gate outside iOS: arm right away.
      try {
        if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission !== "function") {
          armMotion();
        }
      } catch (err) {}


      function wide() {
        return !desktopMQ || desktopMQ.matches;
      }

      // OPTIONAL POINT SOURCE. A section can declare --eddia-smoke-x/y (0..1 of
      // the frame) to say "the smoke starts HERE" — over the craft photograph
      // that is the tip of the embroidered flame, where the loose threads leave
      // the fabric, so the haze reads as rising off the stitching instead of as
      // weather. Declared in CSS rather than JS so it can differ per breakpoint,
      // which it must: the photograph is cover-cropped, so the flame sits at a
      // different fraction of the frame on a phone than on a desktop. Leave the
      // properties unset and every existing viewfinder behaves exactly as before.
      var srcX = -1, srcY = -1;
      function readSource() {
        var cs = getComputedStyle(vf);
        var sx = parseFloat(cs.getPropertyValue("--eddia-smoke-x"));
        var sy = parseFloat(cs.getPropertyValue("--eddia-smoke-y"));
        srcX = isNaN(sx) ? -1 : sx;
        srcY = isNaN(sy) ? -1 : sy;
      }

      function resize() {
        var r = vf.getBoundingClientRect();
        if (!r.width || !r.height) return;
        W = r.width;
        H = r.height;
        canvas.width = Math.max(1, Math.round(W * dens));
        canvas.height = Math.max(1, Math.round(H * dens));
        readSource();
      }

      // Narrow viewports run ALMOST the full budget and feed FASTER than
      // desktop: there is no cursor there to stir extra puffs into the frame,
      // so the ambient haze alone has to carry the effect — thin it out and
      // the whole thing disappears on a phone.
      // A plume is a continuous body of smoke, not a sequence of puffs, and the
      // only way a stamp system reads as continuous is MANY overlapping stamps
      // at LOW individual opacity. Sixty fat puffs will always look like sixty
      // puffs; two hundred faint ones look like smoke.
      function cap() {
        if (srcX >= 0) return wide() ? 300 : 120;
        return wide() ? SMOKE_MAX : Math.round(SMOKE_MAX * 0.85);
      }
      // A point source also has to be fed more slowly than a whole frame: the
      // ambient rate spreads its puffs over the entire lower half, while a plume
      // drops every one of them on the same spot. A phone needs slower still —
      // the particle budget is barely reduced there but the frame is a quarter
      // of the area, so the same rate packs four times the density.
      function feed() {
        if (srcX >= 0) return wide() ? 0.048 : 0.075;
        return wide() ? 0.06 : 0.05;
      }

      // A plume particle is born off a single point and climbs faster than the
      // ambient haze. Its opacity has to be handled differently, though: the
      // draw loop thins a puff by `200 / size`, which is a NO-OP for anything
      // small, and every plume puff is born in the same spot. Left at ambient
      // values they stack into one opaque disc — a smudge on the photograph
      // rather than smoke. So a plume puff starts large enough to be thinned
      // from birth and carries roughly a third of the ambient peak; the density
      // comes from the column, never from any single stamp.
      function spawn(x, y, energetic, plume) {
        if (parts.length >= cap()) return;
        parts.push({
          x: x,
          y: y,
          vx: (Math.random() - 0.5) * (plume ? 16 : energetic ? 60 : 22),
          vy: plume ? -(30 + Math.random() * 24) : -(9 + Math.random() * 21),
          // A plume puff is sized as a FRACTION OF THE FRAME, not in absolute
          // pixels: the ambient haze can afford fixed sizes because it is spread
          // over the whole picture, but a column measured in pixels that reads
          // as a wisp across 1440px swallows a 390px phone whole.
          size: plume ? W * (wide() ? 0.034 : 0.046) + Math.random() * W * (wide() ? 0.024 : 0.03) : (energetic ? 110 : 140) + Math.random() * 170,
          grow: plume ? W * (0.018 + Math.random() * 0.014) : 20 + Math.random() * 32,
          plume: !!plume,
          // Heat is what makes a plume behave like smoke off a flame rather than
          // fog: it is spent as the puff climbs, so the column shoots up while it
          // is hot and slews as it cools and mixes with still air.
          heat: 1,
          rot: Math.random() * Math.PI * 2,
          vrot: (Math.random() - 0.5) * 0.3,
          age: 0,
          life: plume ? 8.5 + Math.random() * 6 : 7 + Math.random() * 5,
          // A phone fits far fewer overlapping puffs into the column, so the
          // same per-particle opacity that reads on a 1440px frame all but
          // vanishes on a 390px one. Raising THIS rather than the particle
          // count is what makes the plume legible on a phone for free: the
          // number of draw calls does not change at all.
          peak: plume
            ? (wide() ? 0.07 + Math.random() * 0.06 : 0.15 + Math.random() * 0.09)
            : 0.38 + Math.random() * 0.26,
          seed: Math.random() * 100,
          sp: (Math.random() * 3) | 0,
          fx: Math.random() < 0.5 ? -1 : 1
        });
      }

      function frame(now) {
        raf = null;
        var dt = last ? (now - last) / 1000 : 0.016;
        var tt = now * 0.0007;
        last = now;
        if (dt > 0.05) dt = 0.05; // a backgrounded tab must not teleport the smoke

        // THE SCROLL IS THE WIND. A phone has no cursor to push the smoke with,
        // so the page's own movement does it: scrolling drags the frame through
        // still air and the haze is left behind, streaming and breaking up. It
        // is expressed as a velocity the air WANTS the smoke to travel at, and
        // the smoke relaxes towards it — an impulse added per frame instead
        // would compound against the drag and fire particles off the canvas.
        var sy = window.pageYOffset || 0;
        var ds = lastScroll === null ? 0 : sy - lastScroll;
        lastScroll = sy;
        if (ds > 140) ds = 140;          // anchor jumps must not become a gale
        if (ds < -140) ds = -140;
        var scrollVel = ds / dt;
        if (scrollVel > 2600) scrollVel = 2600;
        if (scrollVel < -2600) scrollVel = -2600;
        var gust = Math.abs(scrollVel);
        // scrolling down moves the frame up, so the smoke lags downwards
        var wind = scrollVel * 0.42;

        // device-motion air bleeds off quickly: a tilt reads as one gust
        airX *= Math.pow(0.22, dt);
        airY *= Math.pow(0.22, dt);
        var airMag = Math.abs(airX) + Math.abs(airY);

        // THE AMBIENT BREEZE (phones): the haze is never still. Two slow,
        // incommensurate sines wander the air's direction so the smoke sways
        // and streams on its own — the "alive in the hand" feel, delivered
        // without touching a single sensor.
        // A PLUME BARELY FEELS IT. The breeze exists because a phone has no
        // cursor to stir ambient haze with, so the air has to move on its own.
        // A column does not need that — it already has the curl field and its
        // own vortex shedding — and at the ambient strength the breeze simply
        // blows the smoke off its source and across the frame, which is exactly
        // what it was doing: the plume's centre of mass sat at 0.34 of the
        // width while the flame it is supposed to be leaving sat at 0.53. Left
        // in at a fraction it reads as a room with air in it, which is right.
        var breeze = wide() ? 0
          : (Math.sin(now * 0.00037) + Math.sin(now * 0.00013 + 2.1) * 0.6) * (srcX >= 0 ? 15 : 70);

        // the feed: a tight plume off the declared source, or — where no source
        // is declared — ambient haze rising off the lower half of the frame.
        // A source BREATHES: a real flame guts and flares, so its output is
        // modulated by two incommensurate sines rather than held constant. A
        // metronomic feed is one of the things the eye reads as machinery.
        var breath = srcX >= 0
          ? 1 + Math.sin(now * 0.0011) * 0.42 + Math.sin(now * 0.00043 + 1.3) * 0.26
          : 1;
        var step = feed() / Math.max(0.35, breath);
        spawnAcc += dt;
        while (spawnAcc > step) {
          spawnAcc -= step;
          if (srcX >= 0) {
            // a tight mouth: a wide one gives a fan, and smoke off a point
            // leaves as a thread
            spawn(W * srcX + (Math.random() - 0.5) * W * 0.007,
                  H * srcY + (Math.random() - 0.5) * H * 0.012, false, true);
          } else {
            spawn(Math.random() * W, H * (0.55 + Math.random() * 0.55), false);
          }
        }

        // the hand disturbs the air: a puff peels off wherever it moves fast
        var speed = Math.sqrt(mvx * mvx + mvy * mvy);
        if (hasPointer && speed > 190) {
          trailAcc += dt;
          if (trailAcc > 0.028) {
            trailAcc = 0;
            spawn(mx + (Math.random() - 0.5) * 46, my + (Math.random() - 0.5) * 46, true);
          }
        }

        // a hard scroll kicks more smoke up off the whole frame, so the section
        // visibly churns while it is being moved rather than merely sliding
        if (gust > 700 || airMag > 480) {
          gustAcc += dt;
          if (gustAcc > 0.05) {
            gustAcc = 0;
            if (srcX >= 0) {
              // a gust makes the source flare rather than lifting soot off the
              // whole photograph, which would give the plume away as weather
              spawn(W * srcX + (Math.random() - 0.5) * W * 0.05,
                    H * srcY + (Math.random() - 0.5) * H * 0.06, true, true);
            } else {
              spawn(Math.random() * W, H * (0.2 + Math.random() * 0.85), true);
            }
          }
        }

        ctx.setTransform(dens, 0, 0, dens, 0, 0);
        ctx.clearRect(0, 0, W, H);

        for (var i = parts.length - 1; i >= 0; i--) {
          var p = parts[i];
          p.age += dt;
          if (p.age >= p.life) {
            parts.splice(i, 1);
            continue;
          }

          // buoyancy, plus a coherent turbulence field: neighbours sample the
          // same slow-moving field, so the haze folds and curls as one fluid
          // instead of specks wandering alone — the core of looking real
          var ffx = Math.sin(p.y * 0.011 + tt * 2.1 + Math.sin(p.x * 0.006 + tt)) + Math.sin(p.y * 0.023 - tt * 3.2) * 0.5;
          var ffy = Math.sin(p.x * 0.009 - tt * 1.6 + Math.sin(p.y * 0.005 + tt * 0.7)) * 0.7;

          if (p.plume) {
            // THE LAMINAR STEM. This is the single thing that separates smoke
            // off a flame from fog: it leaves the source as a thin, almost
            // straight ribbon, holds that shape for a while, and only then
            // trips into turbulence and starts to roll. Turbulence is therefore
            // ramped by how far the puff has CLIMBED, not by its age — a puff
            // held down by a gust stays laminar, exactly as real smoke does.
            var climbed = (H * srcY - p.y) / (H * 0.42);
            var turb = climbed <= 0 ? 0 : Math.min(1, climbed * climbed * 1.9);

            // Heat spends itself; lift goes with it. Hot and fast at the base,
            // slow and wandering by the top — that deceleration is what reads
            // as air, and a constant rise never does.
            p.heat *= Math.pow(0.62, dt);
            p.vy -= (30 * p.heat + 3.5) * dt;

            // advected by the divergence-free field, so it folds instead of
            // clumping; scaled by the stem ramp so the base stays coherent
            p.vx += curlX(p.x, p.y, tt) * 8200 * turb * dt;
            p.vy += curlY(p.x, p.y, tt) * 8200 * turb * dt;
            // vortex shedding: a rising column destabilises by rolling off
            // alternate sides, which is what puts real curl in the smoke
            p.vx += Math.sin(p.age * 2.7 + p.seed * 1.7) * 42 * turb * dt;
            // tumbling: a billow that is folding is also rolling
            p.rot += p.vrot * (1 + turb * 5) * dt;
            // and the stem stays narrow: it only blooms once it has tripped
            p.size += p.grow * (0.20 + turb * 1.25) * dt;
          } else {
            p.vy -= 9 * dt;
            p.vx += (ffx * 42 + Math.sin(p.age * 0.7 + p.seed) * 5) * dt;
            p.vy += ffy * 28 * dt;
            p.size += p.grow * dt;
          }

          // carried by the scroll wind, and stirred sideways by it so the
          // haze breaks and curls instead of sliding as one sheet
          if (gust > 40) {
            p.vy += (wind - p.vy) * Math.min(1, dt * 3.6);
            p.vx += Math.sin(p.seed * 3.1 + p.age * 2.4) * gust * dt * 0.55;
          }

          // carried by the device-motion air the same way — with its own
          // sideways churn so a tilt breaks the haze up instead of shoving
          // it as one block
          if (airMag > 30) {
            p.vx += (airX - p.vx) * Math.min(1, dt * 3.2);
            p.vy += (airY - p.vy) * Math.min(1, dt * 3.2);
            p.vx += Math.sin(p.seed * 2.7 + p.age * 2.1) * Math.min(airMag, 500) * dt * 0.35;
          }

          // the ambient breeze couples softly, so the turbulence still folds
          // the haze while the whole volume leans with the current
          if (breeze) {
            p.vx += (breeze - p.vx) * Math.min(1, dt * 0.55);
          }

          // the cursor's own velocity is pushed into whatever is near it —
          // this is what makes the smoke answer the hand rather than ignore it
          if (hasPointer && speed > 12) {
            var dx = p.x - mx, dy = p.y - my;
            var d = Math.sqrt(dx * dx + dy * dy);
            if (d < 320) {
              var f = (1 - d / 320) * dt * 3.4;
              p.vx += mvx * f;
              p.vy += mvy * f;
              // and a little swirl around the pointer, so it curls
              p.vx += -dy * f * 0.6;
              p.vy += dx * f * 0.6;
            }
          }

          // A plume keeps more of its momentum than drifting haze does: the
          // column has to hold its line long enough to read as a stem.
          var drag = Math.pow(p.plume ? 0.78 : 0.6, dt);
          p.vx *= drag;
          p.vy *= drag;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.rot += p.vrot * dt;

          var k = p.age / p.life;
          // The stem is dense the instant it leaves the source, so a plume puff
          // fades in almost at once; ambient haze drifts in.
          var fadeIn = p.plume ? 0.07 : 0.22;
          var alpha = k < fadeIn ? k / fadeIn : 1 - (k - fadeIn) / (1 - fadeIn);
          alpha *= p.peak;
          // a puff that has swollen large is also thinner — the same soot
          // spread over more air. The reference is a share of the frame for a
          // plume, whose sizes are frame-relative, and a fixed 200px for the
          // ambient haze, whose sizes are not.
          alpha *= Math.min(1, (p.plume ? W * 0.15 : 200) / p.size);
          if (alpha <= 0.002) continue;

          ctx.globalAlpha = alpha;
          ctx.save();
          ctx.translate(p.x, p.y);
          if (p.plume) {
            // Smoke is not made of discs. A puff travelling fast is drawn out
            // into a filament along its own path and only rounds off as it
            // slows, which is why a stem reads as a ribbon and the top as
            // billows — from one sprite, by stretching it along the velocity.
            var spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
            var st = 1 + Math.min(1.15, spd / 190);
            ctx.rotate(Math.atan2(p.vy, p.vx) + p.rot * 0.35);
            ctx.scale(st, 1.12 / st);
            ctx.drawImage(spritesAsh[p.sp], -p.size / 2, -p.size / 2, p.size, p.size);
            // LIT BY ITS OWN FIRE. While a puff still carries heat it is close
            // enough to the flame to be lit by it, so the ember stamp is laid
            // over the ash one with `lighter` — added as light, not painted as
            // colour — and fades out as the heat goes. This is what warms the
            // base of the column and lets it cool to grey on the way up.
            if (p.heat > 0.08) {
              ctx.globalCompositeOperation = "lighter";
              ctx.globalAlpha = alpha * p.heat * 1.15;
              ctx.drawImage(spritesEmber[p.sp], -p.size / 2, -p.size / 2, p.size, p.size);
            }
          } else {
            ctx.rotate(p.rot);
            if (p.fx < 0) ctx.scale(-1, 1);
            ctx.drawImage(sprites[p.sp], -p.size / 2, -p.size / 2, p.size, p.size);
          }
          ctx.restore();
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";

        // EMBERS. Only where a source is declared, and only now and then: a
        // spark every second or so, riding the same air as the smoke but
        // lighter and faster, guttering out before it clears the frame. They
        // are drawn last and additively so they sit as light on top of the
        // haze, and each one flickers on its own clock — a steady dot would
        // read as a dead pixel, not a coal.
        if (srcX >= 0) {
          emberAcc += dt;
          if (emberAcc > 0.55 + Math.random() * 1.5) {
            emberAcc = 0;
            embers.push({
              x: W * srcX + (Math.random() - 0.5) * W * 0.012,
              y: H * srcY + (Math.random() - 0.5) * H * 0.02,
              vx: (Math.random() - 0.5) * 26,
              vy: -(52 + Math.random() * 46),
              // scaled by frame, but a phone needs a bigger share or the coal
              // lands under two pixels and reads as a stuck sub-pixel
              r: Math.max(1.5, W * (wide() ? 0.0011 : 0.0036) + Math.random() * W * (wide() ? 0.0013 : 0.003)),
              age: 0,
              life: 1.5 + Math.random() * 1.9,
              seed: Math.random() * 100
            });
          }
          ctx.globalCompositeOperation = "lighter";
          for (var e = embers.length - 1; e >= 0; e--) {
            var em = embers[e];
            em.age += dt;
            if (em.age >= em.life) { embers.splice(e, 1); continue; }
            em.vy -= 16 * dt;
            em.vx += curlX(em.x, em.y, tt) * 5200 * dt + Math.sin(em.age * 4.1 + em.seed) * 26 * dt;
            em.vy += curlY(em.x, em.y, tt) * 5200 * dt;
            var ed = Math.pow(0.72, dt);
            em.vx *= ed; em.vy *= ed;
            em.x += em.vx * dt;
            em.y += em.vy * dt;
            var ek = em.age / em.life;
            // guttering: the coal pulses as it burns down, then goes out
            var flick = 0.55 + 0.45 * Math.sin(em.age * 17 + em.seed * 3);
            var ea = (1 - ek) * (1 - ek) * flick;
            if (ea <= 0.01) continue;
            var eg = ctx.createRadialGradient(em.x, em.y, 0, em.x, em.y, em.r * 4.5);
            eg.addColorStop(0, "rgba(255,214,150," + (ea * 0.95).toFixed(3) + ")");
            eg.addColorStop(0.32, "rgba(255,138,44," + (ea * 0.55).toFixed(3) + ")");
            eg.addColorStop(1, "rgba(255,96,20,0)");
            ctx.fillStyle = eg;
            ctx.beginPath();
            ctx.arc(em.x, em.y, em.r * 4.5, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalCompositeOperation = "source-over";
        }

        // the pointer impulse is per-move, so bleed it off every frame
        mvx *= 0.86;
        mvy *= 0.86;

        if (onScreen) {
          raf = window.requestAnimationFrame(frame);
        } else {
          last = 0;
        }
      }

      function start() {
        if (raf || !onScreen) return;
        if (!W || !H) resize();
        if (!W || !H) return;
        last = 0;
        raf = window.requestAnimationFrame(frame);
      }
      function stop() {
        if (raf) window.cancelAnimationFrame(raf);
        raf = null;
        last = 0;
      }

      // a finger lands: the air jumps. Everything near the touch is shoved
      // outward with a sideways curl — like clapping a hand into haze — and a
      // few energetic puffs kick up off the spot itself.
      function burst(x, y) {
        for (var i = 0; i < parts.length; i++) {
          var p = parts[i];
          var dx = p.x - x, dy = p.y - y;
          var d = Math.sqrt(dx * dx + dy * dy) || 1;
          if (d < 260) {
            var f = (1 - d / 260) * 340;
            p.vx += (dx / d) * f - (dy / d) * f * 0.45;
            p.vy += (dy / d) * f + (dx / d) * f * 0.45;
          }
        }
        for (var n = 0; n < 5; n++) {
          spawn(x + (Math.random() - 0.5) * 70, y + (Math.random() - 0.5) * 70, true);
        }
      }

      host.addEventListener("pointerdown", function (e) {
        var r = vf.getBoundingClientRect();
        if (!r.width) return;
        var nx = e.clientX - r.left;
        var ny = e.clientY - r.top;
        if (nx < 0 || ny < 0 || nx > r.width || ny > r.height) return;
        // seed the position FIRST so the next move's delta is honest — an
        // unseeded first delta is what used to blow the haze across the frame
        mx = nx;
        my = ny;
        mvx = 0;
        mvy = 0;
        hasPointer = true;
        if (e.pointerType !== "mouse") {
          armMotion();
          burst(nx, ny);
        }
      }, { passive: true });

      host.addEventListener("pointermove", function (e) {
        // a mouse stirs by mere presence; a finger only stirs while it is
        // actually down (a vertical scroll fires pointercancel and lets go)
        if (e.pointerType !== "mouse" && !hasPointer) return;
        var r = vf.getBoundingClientRect();
        if (!r.width) return;
        var nx = e.clientX - r.left;
        var ny = e.clientY - r.top;
        if (hasPointer) {
          // raw per-event delta, scaled to read as a velocity
          mvx = (nx - mx) * 26;
          mvy = (ny - my) * 26;
          if (mvx > 3000) mvx = 3000;
          if (mvx < -3000) mvx = -3000;
          if (mvy > 3000) mvy = 3000;
          if (mvy < -3000) mvy = -3000;
        }
        mx = nx;
        my = ny;
        hasPointer = true;
      }, { passive: true });

      function releasePointer() {
        hasPointer = false;
        mvx = 0;
        mvy = 0;
      }
      host.addEventListener("pointerup", releasePointer, { passive: true });
      host.addEventListener("pointercancel", releasePointer, { passive: true });
      host.addEventListener("pointerleave", releasePointer, { passive: true });

      window.addEventListener("resize", function () {
        resize();
        // a window widened past the desktop breakpoint has to pick the loop
        // back up; start() is a no-op whenever it is already running
        start();
      }, { passive: true });

      if (supportsIO) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            onScreen = entry.isIntersecting;
            if (onScreen) { resize(); start(); } else { stop(); }
          });
        }, { threshold: 0.01 }).observe(vf);
      } else {
        onScreen = true;
        resize();
        start();
      }
    });
  }

  /* ========================================================================
     2f) Showcase tiles (desktop, mouse only) — one listener per media-grid
     tile drives both the cue disc that rides the cursor (--eddia-cx/cy in px)
     and the photograph's drift against the hand (--eddia-px/py, -1..1).
     Everything it writes is consumed by `translate` alone, so the theme's own
     hover zoom (which animates `scale`) is never touched, and a tile whose
     listener never runs is simply a still photograph.
     ====================================================================== */
  function setupShowcase(scope) {
    if (designMode) return;
    (scope || document).querySelectorAll(".media-grid__item").forEach(function (item) {
      if (item.__eddiaTile) return;
      item.__eddiaTile = true;
      var cue = item.querySelector(".eddia-cue");
      // measure against the media box, not the link: a padded tile insets it
      var host = item.querySelector(".content-over-media") || item;
      var raf = null, cx = 0, cy = 0, px = 0, py = 0;
      function apply() {
        raf = null;
        if (cue) {
          cue.style.setProperty("--eddia-cx", cx.toFixed(1) + "px");
          cue.style.setProperty("--eddia-cy", cy.toFixed(1) + "px");
        }
        item.style.setProperty("--eddia-px", px.toFixed(3));
        item.style.setProperty("--eddia-py", py.toFixed(3));
      }
      item.addEventListener("pointermove", function (e) {
        if (!desktopMQ || !desktopMQ.matches) return;
        if (e.pointerType && e.pointerType !== "mouse") return;
        var r = host.getBoundingClientRect();
        if (!r.width || !r.height) return;
        cx = e.clientX - r.left;
        cy = e.clientY - r.top;
        px = (cx / r.width - 0.5) * 2;
        py = (cy / r.height - 0.5) * 2;
        if (!raf) raf = window.requestAnimationFrame(apply);
      }, { passive: true });
      item.addEventListener("pointerleave", function () {
        px = 0;
        py = 0;
        if (!raf) raf = window.requestAnimationFrame(apply);
      }, { passive: true });
    });
  }

  /* ========================================================================
     2d) Magnetic CTA (desktop, mouse only) — the section's buttons lean a few
     px toward the cursor and spring back on leave. Individual `translate`
     only; CSS supplies the eased spring.
     ====================================================================== */
  function setupMagnetic(scope) {
    if (designMode) return;
    (scope || document).querySelectorAll(".multiple-media-with-text .button-group .button, .slideshow .button-group .button").forEach(function (btn) {
      if (btn.__eddiaMag) return;
      btn.__eddiaMag = true;
      btn.addEventListener("pointermove", function (e) {
        if (!desktopMQ || !desktopMQ.matches) return;
        if (e.pointerType && e.pointerType !== "mouse") return;
        var r = btn.getBoundingClientRect();
        if (!r.width) return;
        var x = (e.clientX - r.left - r.width / 2) * 0.22;
        var y = (e.clientY - r.top - r.height / 2) * 0.3;
        btn.style.translate = x.toFixed(1) + "px " + y.toFixed(1) + "px";
      }, { passive: true });
      btn.addEventListener("pointerleave", function () {
        btn.style.translate = "";
      }, { passive: true });
    });
  }

  /* ========================================================================
     3) Scroll progress bar (optional, minimal)
     ====================================================================== */
  var progressBar = null;
  function setupProgress() {
    if (!CFG.progress || designMode) return;
    progressBar = document.createElement("div");
    progressBar.className = "eddia-scroll-progress";
    progressBar.setAttribute("aria-hidden", "true");
    document.body.appendChild(progressBar);
  }

  /* ========================================================================
     4) Parallax (subtle, desktop only) — native "Personalization" media
     ====================================================================== */
  var parallaxEls = [];
  var desktopMQ = null;
  function collectParallax() {
    if (!CFG.parallax) return;
    // Editorial media tiles. We translate the whole tile (individual `translate`
    // property) so it composes with the theme's `transform: rotate()` and never
    // clips. `[data-eddia-parallax-target]` lets a merchant opt any block in.
    document.querySelectorAll(
      ".multiple-media-with-text__media-wrapper > div, [data-eddia-parallax-target]"
    ).forEach(function (el) {
      el.setAttribute("data-eddia-parallax", "");
    });
    // Rebuild from the DOM so a Theme-Editor re-scan keeps every tagged tile in
    // the loop (a destructive reset would freeze tiles outside the reloaded one).
    parallaxEls = Array.prototype.slice.call(
      document.querySelectorAll("[data-eddia-parallax]")
    );
  }
  function clearParallax() {
    parallaxEls.forEach(function (el) {
      el.style.removeProperty("--eddia-py");
    });
  }

  /* ========================================================================
     Shared rAF scroll loop (progress + parallax)
     ====================================================================== */
  var ticking = false;
  var heroEl = null;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }
  function update() {
    ticking = false;
    var vh = window.innerHeight || document.documentElement.clientHeight;

    /* Hero exit progress (0 at top → 1 when the hero has scrolled away).
       CSS uses --eddia-hp for the cinematic depth-exit. */
    if (heroEl) {
      var hr = heroEl.getBoundingClientRect();
      if (hr.height) {
        var hp = Math.min(1, Math.max(0, -hr.top / hr.height));
        heroEl.style.setProperty("--eddia-hp", hp.toFixed(4));
      }
    }

    if (progressBar) {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      var docH = document.documentElement.scrollHeight - vh;
      var p = docH > 0 ? Math.min(1, Math.max(0, scrollTop / docH)) : 0;
      progressBar.style.setProperty("--eddia-progress", p.toFixed(4));
    }

    var doParallax = parallaxEls.length && desktopMQ && desktopMQ.matches;
    if (doParallax) {
      var range = 10; // px of drift; kept under the grid gap so tiles never overlap
      for (var i = 0; i < parallaxEls.length; i++) {
        var el = parallaxEls[i];
        var r = el.getBoundingClientRect();
        if (r.bottom < -40 || r.top > vh + 40) continue; // off-screen: skip work
        var center = r.top + r.height / 2;
        var ratio = (center - vh / 2) / (vh / 2); // -1 (top) .. 1 (bottom)
        var y = Math.max(-1, Math.min(1, ratio)) * -range;
        el.style.setProperty("--eddia-py", y.toFixed(1) + "px");
      }
    }
  }

  /* ========================================================================
     9) Offices — the house clocks
     A brand that prints three addresses should know what time it is at each of
     them. Intl does the whole job natively: no library, no network request, no
     clock to keep in sync. A clock only becomes visible once its time zone
     really resolves, so a typo in the IANA name drops that readout instead of
     printing a broken one. One shared interval paints every clock; nothing is
     animated, the text simply changes on the minute.
     ====================================================================== */
  var clocks = [];
  var clockTimer = null;

  function paintClocks() {
    var now = new Date();
    for (var i = clocks.length - 1; i >= 0; i--) {
      var c = clocks[i];
      // a section re-rendered in the editor leaves detached rows behind
      if (!c.value.isConnected) { clocks.splice(i, 1); continue; }
      try {
        c.value.textContent = c.fmt.format(now);
        c.row.classList.add("is-live");
      } catch (e) {}
    }
  }

  function setupClocks(scope) {
    scope = scope || document;
    if (!window.Intl || !window.Intl.DateTimeFormat) return;

    scope.querySelectorAll(".eddia-offices [data-eddia-tz]").forEach(function (row) {
      if (row.__eddiaInit) return;
      row.__eddiaInit = true;

      var value = row.querySelector(".eddia-offices__time");
      var zone = row.getAttribute("data-eddia-tz");
      if (!value || !zone) return;

      var fmt;
      try {
        // an unknown IANA zone throws right here, which is what keeps the row hidden
        fmt = new Intl.DateTimeFormat([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
          timeZone: zone
        });
      } catch (e) {
        return;
      }

      clocks.push({ row: row, value: value, fmt: fmt });
    });

    if (!clocks.length) return;
    paintClocks();
    if (!clockTimer) clockTimer = setInterval(paintClocks, 30000);
  }

  /* ========================================================================
     Boot
     ====================================================================== */
  function boot() {
    try {
      if (supportsIO) {
        revealObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var t = entry.target;
            if (t.hasAttribute("data-eddia-stagger")) {
              t.querySelectorAll("[data-eddia-reveal]").forEach(markIn);
            } else {
              markIn(t);
            }
            revealObserver.unobserve(t);
          });
        }, { rootMargin: "0px 0px -12% 0px", threshold: 0.01 });

        stepObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-active");
            stepObserver.unobserve(entry.target);
          });
        }, { rootMargin: "0px 0px -15% 0px", threshold: 0.01 });
      }

      setupHeroLetters();
      setupWordReveal(document);
      setupOdometer(document);
      setupReveals(document);
      setupSteps(document);
      setupClocks(document);
      setupProgress();
      heroEl = document.querySelector(".shopify-section--slideshow .slideshow");

      // shared desktop test: the pointer effects below all need it, and the
      // lamp must not depend on whether parallax happens to be switched on
      try { desktopMQ = window.matchMedia("(min-width: 1000px)"); } catch (e) {}
      setupLamp(document);
      setupShowcase(document);
      setupNear(document);
      setupRoll(document);
      setupShowroom(document);
      setupMenuLeave(document);
      // its own guard: a canvas failure must never tear down the motion layer
      try { setupSmoke(document); } catch (e) {}

      if (CFG.parallax) {
        collectParallax();
        setupTilt(document);
        setupMagnetic(document);
        if (desktopMQ) {
          var onMQ = function () { if (!desktopMQ.matches) clearParallax(); update(); };
          if (desktopMQ.addEventListener) desktopMQ.addEventListener("change", onMQ);
          else if (desktopMQ.addListener) desktopMQ.addListener(onMQ);
        }
      }

      if (progressBar || parallaxEls.length || heroEl) {
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll, { passive: true });
        update(); // set initial values
      }

      // hero entrance plays once on load; remove the gate so it never re-triggers
      // when a multi-slide carousel moves .is-selected to another slide
      window.setTimeout(function () {
        root.classList.remove("eddia-hero-intro");
      }, 2500);

      // hard failsafe: never leave content hidden if an observer misfires
      window.setTimeout(function () {
        var vh = window.innerHeight || 0;
        document.querySelectorAll("[data-eddia-reveal]:not(.is-in)").forEach(function (el) {
          if (el.getBoundingClientRect().top < vh) markIn(el);
        });
        document.querySelectorAll(".eddia-steps__reveal:not(.is-active)").forEach(function (el) {
          if (el.getBoundingClientRect().top < vh) el.classList.add("is-active");
        });
      }, 2600);

      // Theme Editor: re-init a section when it is added/edited, and never leave
      // an edited section hidden.
      if (designMode) {
        document.addEventListener("shopify:section:load", function (e) {
          var s = e.target;
          if (!s) return;
          setupWordReveal(s);
          setupOdometer(s);
          setupLamp(s);
          setupShowcase(s);
          setupNear(s);
          setupRoll(s);
          setupShowroom(s);
          setupMenuLeave(s);
          try { setupSmoke(s); } catch (e) {}
          setupReveals(s);
          setupSteps(s);
          setupClocks(s);
          s.querySelectorAll("[data-eddia-reveal]").forEach(markIn);
          s.querySelectorAll(".eddia-steps__reveal").forEach(function (l) { l.classList.add("is-active"); });
          collectParallax();
        });
        document.addEventListener("shopify:section:select", function (e) {
          if (!e.target) return;
          e.target.querySelectorAll("[data-eddia-reveal]").forEach(markIn);
        });
      }

      // tell the inline watchdog we booted OK, so it won't reveal-all
      window.__eddiaMotionReady = true;
    } catch (err) {
      panic(err);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();

/* ============================================================================
   Eddia — plate caption index (product gallery)
   ----------------------------------------------------------------------------
   Deliberately OUTSIDE the motion controller above. The caption under the
   product plate reports which shot is on screen, and that is information, not
   decoration — it has to stay correct when the merchant turns motion off and
   when the visitor asks for reduced motion, both of which shut the controller
   down before it reaches its first module.

   The theme already keeps aria-current honest on the thumbnail strip and on the
   mobile dots, so the number is read from whichever control is present rather
   than from scroll position. One MutationObserver per gallery, no listeners, no
   polling; if anything at all goes wrong the caption simply keeps the value
   Liquid printed.
   ========================================================================== */
(function () {
  "use strict";

  function pad(n) {
    return n < 10 ? "0" + n : String(n);
  }

  function initGallery(gallery) {
    if (gallery.__eddiaPlate) return;

    var indexEl = gallery.querySelector("[data-eddia-plate-index]");
    if (!indexEl) return;
    gallery.__eddiaPlate = true;

    var totalEl = gallery.querySelector(".eddia-plate__total");

    function paint() {
      var lists = gallery.querySelectorAll("product-gallery-navigation, carousel-navigation");

      for (var i = 0; i < lists.length; i++) {
        var buttons = [];
        var all = lists[i].querySelectorAll("button");

        for (var k = 0; k < all.length; k++) {
          // variant image sets hide the shots that belong to other colours
          if (!all[k].hasAttribute("hidden")) buttons.push(all[k]);
        }
        if (!buttons.length) continue;

        for (var j = 0; j < buttons.length; j++) {
          if (buttons[j].getAttribute("aria-current") !== "true") continue;

          indexEl.textContent = pad(j + 1);
          if (totalEl) totalEl.textContent = "/ " + pad(buttons.length);
          return;
        }
      }
    }

    try {
      new MutationObserver(paint).observe(gallery, {
        subtree: true,
        attributes: true,
        attributeFilter: ["aria-current", "hidden"]
      });
    } catch (e) {}

    paint();
  }

  function scan(scope) {
    try {
      (scope || document).querySelectorAll(".product-gallery").forEach(initGallery);
    } catch (e) {}
  }

  function start() {
    scan(document);

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener("shopify:section:load", function (e) {
        if (e.target) scan(e.target);
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();

/* ============================================================================
   Eddia - video hardening
   ----------------------------------------------------------------------------
   Outside the motion controller: this is not decoration, and it has to hold
   when the motion layer is off or reduced motion is asked for.

   Be clear about what this is. A web video cannot be made undownloadable - the
   browser fetches the file over HTTP and anyone who opens the network panel is
   already holding it. Real protection is signed, DRM-wrapped streaming, which
   Shopify's native video hosting does not offer. What this does is close every
   door the browser opens by itself:

     controlsList=nodownload   removes the download item from any control menu
     disablePictureInPicture   removes "picture in picture"
     disableRemotePlayback     removes AirPlay / Cast, which mirror the file out
     contextmenu               removes right-click "Save video as..."

   Liquid's video_tag filter writes none of these, which is why they are set
   here rather than in the section.
   ========================================================================== */
(function () {
  "use strict";

  function harden(video) {
    if (video.__eddiaHardened) return;
    video.__eddiaHardened = true;

    try {
      video.setAttribute("controlsList", "nodownload noplaybackrate noremoteplayback");
      video.setAttribute("disablepictureinpicture", "");
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
    } catch (e) {}

    video.addEventListener("contextmenu", function (event) {
      event.preventDefault();
    });
    video.addEventListener("dragstart", function (event) {
      event.preventDefault();
    });
  }

  function scan(scope) {
    try {
      (scope || document).querySelectorAll("video").forEach(harden);
    } catch (e) {}
  }

  function start() {
    scan(document);

    // <video-media> mounts its player when the section scrolls into view, and
    // the theme editor rebuilds whole sections, so watch for both rather than
    // assuming every video is in the document at parse time
    try {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (node.tagName === "VIDEO") harden(node);
            else scan(node);
          }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();

/* ============================================================================
   Eddia - video hero, adaptive contrast
   ----------------------------------------------------------------------------
   Outside the motion controller: keeping type legible is not decoration, and it
   has to hold when the motion layer is off or reduced motion is asked for.

   WHAT IT MEASURES
   The frame behind the WORDS, not the frame as a whole. The text block's
   rectangle is projected back into the video's own pixel space (the film is
   object-fit: cover, so that projection has to account for the crop), and a
   20x12 thumbnail of exactly that region is read back. 240 pixels, a few times
   a second - cheap enough to be honest about.

   WHAT IT DECIDES
   Luminance is linearised first, because contrast ratios are defined on light,
   not on the gamma-encoded numbers in the buffer. White type and #1c1c1c type
   give equal contrast at a luminance of about 0.204, so that - not the middle
   grey people reach for - is the crossover, and it is fenced with hysteresis so
   a frame hovering on the line cannot oscillate.

   The veil each colour would need to reach 4.5:1 is still SOLVED rather than
   guessed - alpha compositing is linear in linear light, so it has a closed
   form - but nothing is veiled any more. The two answers are a price list: the
   colour that would cost the film less is the colour the type takes, and the
   film itself is left exactly as it was shot.

   The spread of the sample drives the text-shadow separately, because a mean
   says nothing about a frame that is half highlight and half shadow behind one
   line of type. It moves the shadow's OPACITY only; the geometry is fixed in
   the stylesheet at 1px and 2px, so the mark can never grow into a shape.

   WHAT IT NEVER DOES
   Touch a style directly, or paint anything behind the words. It writes two
   numbers; the transitions and every colour derived from them live in the
   stylesheet.
   ========================================================================== */
(function () {
  "use strict";

  var TARGET_RATIO = 4.5;
  var SCRIM_MAX = 0.5; // cap on the notional veil, so the price list stays sane
  // Ink type is only worth crossing to when the frame is bright enough that it
  // would be the cheap answer by a clear margin - otherwise white type is both
  // the safer read and the more familiar one for a film behind a headline.
  var INK_NEEDS_AT_MOST = 0.06;
  var INK_WORTH_IT_ABOVE = 0.18;
  var CONFIRMATIONS = 4;
  var SAMPLE_W = 20;
  var SAMPLE_H = 12;
  var INTERVAL = 140; // ms between reads
  var EASE = 0.2; // exponential smoothing on every measurement

  // luminance of the two notional veils and of the ink, in linear light
  var L_DARK = 0.0025;
  var L_LIGHT = 0.94;
  var L_INK = 0.0116; // #1c1c1c

  // sRGB -> linear, once
  var LINEAR = new Float32Array(256);
  (function () {
    for (var i = 0; i < 256; i++) {
      var c = i / 255;
      LINEAR[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }
  })();

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  function setup(slide) {
    if (slide.__eddiaHeroContrast) return;

    var content = slide.querySelector(".slideshow__slide-content");
    var videos = slide.querySelectorAll("video");
    if (!content || !videos.length) return;

    slide.__eddiaHeroContrast = true;

    var canvas = document.createElement("canvas");
    canvas.width = SAMPLE_W;
    canvas.height = SAMPLE_H;

    var ctx = null;
    try {
      ctx = canvas.getContext("2d", { willReadFrequently: true });
    } catch (e) {}
    if (!ctx) return;

    // three points on the distribution, not one average: the bright end is what
    // threatens white type, the dark end is what threatens ink type, and a
    // frame can hold both at once
    var lo = null;        // smoothed 15th percentile luminance
    var hi = null;        // smoothed 85th percentile
    var light = true;     // is the type currently white?
    var pending = 0;      // consecutive reads asking for the other mode
    var lums = new Float32Array(SAMPLE_W * SAMPLE_H);
    var visible = false;
    var alive = true;
    var last = 0;

    function activeVideo() {
      for (var i = 0; i < videos.length; i++) {
        var v = videos[i];
        // the theme ships a desktop and a mobile copy and hides one of them
        if (v.offsetParent !== null && v.getBoundingClientRect().width > 0) return v;
      }
      return null;
    }

    function read(video) {
      var media = video.getBoundingClientRect();
      var text = content.getBoundingClientRect();
      var vw = video.videoWidth;
      var vh = video.videoHeight;
      if (!vw || !vh || !media.width || !text.width) return null;

      // object-fit: cover - the film is scaled to the larger of the two ratios
      // and centred, so part of it is off the box on one axis
      var scale = Math.max(media.width / vw, media.height / vh);
      var ox = media.left + (media.width - vw * scale) / 2;
      var oy = media.top + (media.height - vh * scale) / 2;

      var sx = (text.left - ox) / scale;
      var sy = (text.top - oy) / scale;
      var sw = text.width / scale;
      var sh = text.height / scale;

      // a text block can hang past the film's edge; keep the read inside it
      sx = clamp(sx, 0, vw - 1);
      sy = clamp(sy, 0, vh - 1);
      sw = clamp(sw, 1, vw - sx);
      sh = clamp(sh, 1, vh - sy);

      try {
        ctx.drawImage(video, sx, sy, sw, sh, 0, 0, SAMPLE_W, SAMPLE_H);
        return ctx.getImageData(0, 0, SAMPLE_W, SAMPLE_H).data;
      } catch (e) {
        // a tainted canvas means no measurement is possible at all: stand down
        // and leave the stylesheet's own initial values in place
        alive = false;
        return null;
      }
    }

    function paint() {
      var video = activeVideo();
      if (!video || video.readyState < 2) return;

      var data = read(video);
      if (!data) return;

      var count = SAMPLE_W * SAMPLE_H;

      for (var i = 0, j = 0; i < data.length; i += 4, j++) {
        lums[j] = 0.2126 * LINEAR[data[i]] + 0.7152 * LINEAR[data[i + 1]] + 0.0722 * LINEAR[data[i + 2]];
      }
      lums.sort();

      var rawLo = lums[(count * 0.15) | 0];
      var rawHi = lums[(count * 0.85) | 0];

      lo = lo === null ? rawLo : lo + (rawLo - lo) * EASE;
      hi = hi === null ? rawHi : hi + (rawHi - hi) * EASE;

      // --- what would each colour cost the film? ------------------------------
      // Alpha compositing is linear in linear light, so the veil each colour
      // WOULD need to reach a 4.5:1 ratio has a closed form. Nothing is veiled
      // any more - these two numbers are now only a price list, and the cheaper
      // colour wins. White type is threatened by the bright end of the
      // distribution, ink type by the dark end, so each is priced against the
      // percentile that can actually break it rather than against an average
      // that hides both.
      var ceiling = 1.05 / TARGET_RATIO - 0.05;
      var floor = TARGET_RATIO * (L_INK + 0.05) - 0.05;

      var needDark = hi > ceiling ? clamp((hi - ceiling) / (hi - L_DARK), 0, SCRIM_MAX) : 0;
      var needLight = lo < floor ? clamp((floor - lo) / (L_LIGHT - lo), 0, SCRIM_MAX) : 0;

      // --- which colour, then? ------------------------------------------------
      // Ink only when it is nearly free and white is expensive; confirmed over
      // several reads so a passing frame cannot flip the headline and back.
      var wants = needLight <= INK_NEEDS_AT_MOST && needDark >= INK_WORTH_IT_ABOVE ? false : true;

      if (wants !== light) {
        pending++;
        if (pending >= CONFIRMATIONS) {
          light = wants;
          pending = 0;
        }
      } else {
        pending = 0;
      }

      // --- and how firm an edge does a busy frame need? -----------------------
      // The gap between the two ends is the honest measure of "busy". It drives
      // the OPACITY of the glyph edge and nothing else: the shadow's geometry is
      // fixed at 1px and 2px in the stylesheet, so a difficult frame gets a
      // firmer edge on the letters and never a larger shape behind them.
      // Measured on this film: behind the text the frame runs lo~0.10 / hi~0.90
      // for most of its length, so NEITHER type colour clears 4.5:1 there and
      // the edge is the only separation the words get. It is allowed to go
      // nearly solid on frames that bad - a 1px dark line hard against a white
      // stroke is letterpress, not glow, because it never leaves the stroke.
      var edge = clamp(0.30 + (hi - lo) * 0.68, 0.30, 0.85);

      var style = slide.style;
      style.setProperty("--eddia-hero-lum", light ? "1" : "0");
      style.setProperty("--eddia-hero-edge", edge.toFixed(3));
    }

    function tick(now) {
      if (!alive) return;
      window.requestAnimationFrame(tick);

      if (!visible || document.visibilityState !== "visible") return;
      if (now - last < INTERVAL) return;
      last = now;

      try {
        paint();
      } catch (e) {
        alive = false;
      }
    }

    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }, { threshold: 0.05 }).observe(slide);
    } else {
      visible = true;
    }

    window.requestAnimationFrame(tick);
  }

  function scan(scope) {
    try {
      (scope || document).querySelectorAll('.slideshow__slide[media-type="video"]').forEach(setup);
    } catch (e) {}
  }

  function start() {
    scan(document);
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener("shopify:section:load", function (e) {
        if (e.target) scan(e.target);
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();

/* ----------------------------------------------------------------------------
   EDDIA — wholesale minimum message, in English.

   Wholesale twins carry min="100" on the quantity input (snippets/quantity-selector.liquid).
   When the shopper types 99 and submits, theme.js falls back to the browser's own
   constraint validation, and that message is rendered in the BROWSER's language — a
   Turkish Chrome says "Değer 100'den büyük veya buna eşit olmalıdır", which is wrong for
   a storefront that sells in English. Setting a custom validity string overrides the
   localised text with ours, so every shopper sees the same English sentence.
   ---------------------------------------------------------------------------- */
(function () {
  var wording = function (min) {
    return 'Minimum order is ' + min + ' pieces. Please enter ' + min + ' or more.';
  };

  var sync = function (input) {
    if (!input || input.type !== 'number' || !input.classList.contains('quantity-selector__input')) return;
    var min = parseInt(input.getAttribute('min') || '1', 10);
    if (!(min > 1)) {
      if (input.validationMessage) input.setCustomValidity('');
      return;
    }
    var value = parseInt(input.value, 10);
    input.setCustomValidity(isNaN(value) || value < min ? wording(min) : '');
  };

  var onEvent = function (event) {
    sync(event.target);
  };

  ['input', 'change', 'invalid', 'blur'].forEach(function (type) {
    document.addEventListener(type, onEvent, true);
  });

  var seed = function () {
    document.querySelectorAll('.quantity-selector__input').forEach(sync);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', seed);
  } else {
    seed();
  }
  document.addEventListener('shopify:section:load', seed);
})();

/* --------------------------------------------------------------------------------------------------------------------
   WHOLESALE MINIMUM — CHECKOUT GUARD
   --------------------------------------------------------------------------------------------------------------------

   `eddia-wholesale-minimum.liquid` renders a notice whenever a wholesale line sits below
   100 pieces. The notice alone is easy to ignore, so while it is on the page the checkout
   button is held closed. The cart re-renders itself over AJAX and the markup arrives via
   innerHTML — a script tag inside the snippet would never run — so watch the DOM instead
   and re-read the state after every render. */
(function () {
  var NOTICE = '.eddia-wholesale-minimum';
  var CHECKOUT = '[name="checkout"]';

  var apply = function () {
    var blocked = document.querySelector(NOTICE) !== null;
    document.querySelectorAll(CHECKOUT).forEach(function (button) {
      /* Only ever undo our own disabling: a button the theme disabled for its own reasons
         (an empty cart, a request in flight) must stay that way. */
      if (blocked) {
        button.disabled = true;
        button.setAttribute('data-eddia-blocked', '');
      } else if (button.hasAttribute('data-eddia-blocked')) {
        button.disabled = false;
        button.removeAttribute('data-eddia-blocked');
      }
    });
  };

  var queued = false;
  var schedule = function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      apply();
    });
  };

  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', schedule);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
})();

/* --------------------------------------------------------------------------------------------------------------------
   CUSTOMIZE LINK — CARRY THE CHOSEN COLOUR AND QUANTITY
   --------------------------------------------------------------------------------------------------------------------

   The link is rendered with the colour that was selected server-side, but colour and quantity
   then change without a page load. Zakeke's proxy takes `id` (a variant of the MASTER product)
   and `quantity`, so on every change the colour NAME is matched against the master's variants —
   the ids differ per listing, the names do not — and the href is rewritten in place.

   Zakeke's own button on the six masters reads `window.zakekeGetVariant()` first and only falls
   back to the `?variant=` in the address bar, so that is defined here too: the address bar is
   only updated when the picker's `update_url` setting is on, and a stale one sends the customer
   to the wrong colour. */
(function () {
  var LINK = '[data-eddia-customize]';

  var selectedVariantId = function () {
    var input = document.querySelector('product-form input[name="id"], form[action*="/cart/add"] input[name="id"]');
    return input && input.value ? input.value : null;
  };

  window.zakekeGetVariant = selectedVariantId;

  /* The page carries more than one quantity input — a hidden one ships with the sticky buy
     bar and always reads 1. Take the visible one that belongs to a product form, or the
     wholesale minimum silently becomes a single pair. */
  var quantityInput = function () {
    var inputs = Array.prototype.slice.call(document.querySelectorAll('.quantity-selector__input'));
    return (
      inputs.filter(function (i) { return i.offsetParent !== null && i.getAttribute('form'); })[0] ||
      inputs.filter(function (i) { return i.offsetParent !== null; })[0] ||
      inputs[0] ||
      null
    );
  };

  window.zakekeGetQuantity = function () {
    var input = quantityInput();
    if (!input) return null;
    var value = parseInt(input.value, 10);
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(value) || value < 1) return null;
    return !isNaN(min) && value < min ? min : value;
  };

  var colourOf = function (variant) {
    if (!variant) return null;
    /* `options` is the authoritative list; `option1` is there on the older payload shape. */
    if (variant.options && variant.options.length) return variant.options[0];
    return variant.option1 || null;
  };

  var sync = function (variant) {
    document.querySelectorAll(LINK).forEach(function (link) {
      var map;
      try {
        map = JSON.parse(link.getAttribute('data-eddia-variants') || '{}');
      } catch (error) {
        return;
      }

      var url;
      try {
        url = new URL(link.getAttribute('href'), window.location.origin);
      } catch (error) {
        return;
      }

      var colour = colourOf(variant);
      if (colour === null) {
        /* The picker's radio value is an option-value id, not the colour name. The theme
           also prints the selected variant as JSON next to the picker and re-renders it on
           every change — that is the name to trust. */
        var printed = document.querySelector('script[data-variant]');
        if (printed) {
          try {
            colour = colourOf(JSON.parse(printed.textContent || '{}'));
          } catch (error) {
            colour = null;
          }
        }
      }
      /* An unknown colour means the map and the listing have drifted apart. Leave the link on
         whatever Liquid resolved rather than sending the customer to a guessed variant. */
      if (colour !== null && Object.prototype.hasOwnProperty.call(map, colour)) {
        url.searchParams.set('id', map[colour]);
      }

      var quantity = window.zakekeGetQuantity();
      if (quantity) url.searchParams.set('quantity', quantity);

      link.setAttribute('href', url.pathname + url.search);
    });
  };

  document.addEventListener('variant:change', function (event) {
    sync(event.detail && event.detail.variant);
  });
  document.addEventListener('change', function (event) {
    if (event.target.closest && event.target.closest('.quantity-selector')) sync(null);
  });
  document.addEventListener('input', function (event) {
    if (event.target.matches && event.target.matches('.quantity-selector__input')) sync(null);
  });
})();

/* --------------------------------------------------------------------------------------------------------------------
   PRODUCT DESCRIPTION — COLLAPSE THE LONG COPY ON PHONES
   --------------------------------------------------------------------------------------------------------------------

   The description below the gallery is ~2,400 characters of search copy across ten headed
   sections. On a desktop column it reads fine; on a 390px screen it runs about 3,200px of
   flat text and buries everything under it.

   So on phones only, each heading and the block that follows it fold into a <details>. Not a
   word is removed and the markup the server sends is unchanged — the copy stays in the DOM,
   which is what matters for search, and the desktop layout is untouched. The opening heading
   and its paragraph stay visible: that is the pitch, and it should not need a tap. */
(function () {
  var SELECTOR = '.product-content-below-gallery__description';
  var phone = window.matchMedia('(max-width: 699px)');

  var fold = function (root) {
    if (root.hasAttribute('data-eddia-folded')) return;

    var kids = Array.prototype.slice.call(root.children);
    var first = kids.findIndex(function (el) { return el.tagName === 'H3'; });
    if (first < 0) return;

    root.setAttribute('data-eddia-folded', '');
    var i = first;
    while (i < kids.length) {
      var heading = kids[i];
      if (heading.tagName !== 'H3') { i++; continue; }

      var details = document.createElement('details');
      details.className = 'eddia-fold';
      var summary = document.createElement('summary');
      summary.className = 'eddia-fold__summary';
      root.insertBefore(details, heading);
      details.appendChild(summary);
      summary.appendChild(heading);

      var body = document.createElement('div');
      body.className = 'eddia-fold__body';
      details.appendChild(body);

      var j = i + 1;
      while (j < kids.length && kids[j].tagName !== 'H3') {
        body.appendChild(kids[j]);
        j++;
      }
      i = j;
    }

    /* Leave the first section open so the pattern is obvious and the page still reads as copy
       rather than as a bare list of switches. */
    var opening = root.querySelector('.eddia-fold');
    if (opening) opening.open = true;
  };

  var unfold = function (root) {
    if (!root.hasAttribute('data-eddia-folded')) return;
    root.removeAttribute('data-eddia-folded');
    root.querySelectorAll('.eddia-fold').forEach(function (details) {
      var heading = details.querySelector('.eddia-fold__summary > *');
      var body = details.querySelector('.eddia-fold__body');
      if (heading) root.insertBefore(heading, details);
      if (body) while (body.firstChild) root.insertBefore(body.firstChild, details);
      details.remove();
    });
  };

  var apply = function () {
    document.querySelectorAll(SELECTOR).forEach(function (root) {
      phone.matches ? fold(root) : unfold(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
  phone.addEventListener('change', apply);
  document.addEventListener('shopify:section:load', apply);
})();
